/**
 * Your price cards (`/rate-cards`, TMMAPI-0131): the cards the platform granted this reseller.
 * The adopted card first, then by name. No filter and no New. An empty list means nothing has
 * been granted yet. The margin per subscriber is tmm-api's; without an agreement it is the plain
 * words "Needs an agreement" (TMMRSL-0075). The worksheet on each card shows the full margin.
 */
import { Box, Link, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { rateCardsApi, type GrantedCard } from '@/api/rateCards';
import { MoneyCell } from '@/components/cells';
import { ApiErrorState } from '@/components/ApiErrorState';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { useRemote } from '@/components/useRemote';
import { moneyIn } from '@/components/moneyIn';
import { StatusChip } from '@/components/status/StatusChip';
import { intlLocale } from '@/i18n/constants';
import { useLocale } from '@/i18n/useLocale';

const loadCards = () => rateCardsApi.listGrantedCards();

function byAdoptedThenName(locale: string) {
  return (a: GrantedCard, b: GrantedCard) => {
    const adopted = Number(b.adopted) - Number(a.adopted);
    if (adopted !== 0) return adopted;
    return a.name.localeCompare(b.name, intlLocale(locale));
  };
}

export function RateCardsScreen() {
  const { t } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const cards = useRemote(loadCards);
  const dash = t('statements.unavailable');
  const shown = [...(cards.data ?? [])].sort(byAdoptedThenName(locale));

  return (
    <Box>
      <PageHeader id="rateCards" />
      {cards.status === 'error' ? (
        <ApiErrorState what={t('errors.what.rateCards')} error={cards.error} onRetry={cards.reload} />
      ) : null}
      {cards.status === 'loading' && !cards.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}
      {cards.status !== 'error' && cards.data ? (
        cards.data.length === 0 ? (
          <EmptyState kind="none" title={t('rateCards.empty')} />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={t('pages.rateCards.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('rateCards.col.name')}</TableCell>
                  <TableCell align="right">{t('rateCards.col.base')}</TableCell>
                  <TableCell align="right">{t('rateCards.col.annual')}</TableCell>
                  <TableCell align="right">{t('rateCards.col.margin')}</TableCell>
                  <TableCell>{t('rateCards.col.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell component="th" scope="row">
                      <Link
                        component={RouterLink}
                        to={`/rate-cards/${card.id}`}
                        state={{ listSearch: location.search }}
                      >
                        {card.name || t('rateCard.panel.unknown')}
                      </Link>
                    </TableCell>
                    <MoneyCell minor={card.baseMinor} currency={card.currency} />
                    <MoneyCell minor={card.annualBaseMinor} currency={card.currency} />
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {card.marginMinor === null
                        ? t('rateCards.margin.needsAgreement')
                        : moneyIn(card.marginMinor, card.currency, locale, dash)}
                    </TableCell>
                    <TableCell>
                      {card.adopted ? <StatusChip domain="rateCard" status="ADOPTED" /> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )
      ) : null}
    </Box>
  );
}
